
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { authClient } from "@/lib/auth-client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { validatePassword } from "@/lib/password-validation";
import PasswordValidationDisplay from "./PasswordValidationDisplay";
import ValidationDialog from "./ValidationDialog";

const passwordFormSchema = z.object({
  currentPassword: z.string().min(1, {
    message: "Praegune parool on kohustuslik",
  }),
  newPassword: z.string(),
  confirmPassword: z.string(),
}).refine((data) => {
  const validation = validatePassword(data.newPassword);
  return validation.isValid;
}, {
  message: "Parool ei vasta turvalisuse nõuetele",
  path: ["newPassword"],
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Paroolid ei ühti",
  path: ["confirmPassword"],
});

type PasswordFormValues = z.infer<typeof passwordFormSchema>;

interface PasswordChangeFormProps {
  onSuccess: () => void;
  onError: (error: any) => void;
}

const PasswordChangeForm = ({ onSuccess, onError }: PasswordChangeFormProps) => {
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordValidationErrors, setPasswordValidationErrors] = useState<string[]>([]);
  const [showValidationDialog, setShowValidationDialog] = useState(false);

  const passwordForm = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordFormSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    mode: "onChange",
  });

  // Watch for new password changes to show validation errors in real-time
  const newPassword = passwordForm.watch("newPassword");
  
  useEffect(() => {
    if (newPassword) {
      const validation = validatePassword(newPassword);
      setPasswordValidationErrors(validation.errors);
    } else {
      setPasswordValidationErrors([]);
    }
  }, [newPassword]);

  const changePassword = async (values: PasswordFormValues) => {
    setIsChangingPassword(true);
    try {
      const validation = validatePassword(values.newPassword);
      if (!validation.isValid) {
        setShowValidationDialog(true);
        return;
      }

      const { error } = await authClient.changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });

      if (error) throw new Error(error.message ?? "Password change failed");

      passwordForm.reset();
      onSuccess();
    } catch (error: any) {
      console.error("Error changing password:", error);
      onError(error);
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <>
      <Form {...passwordForm}>
        <form
          onSubmit={passwordForm.handleSubmit(changePassword)}
          className="space-y-4 py-4"
        >
          <FormField
            control={passwordForm.control}
            name="currentPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Praegune parool</FormLabel>
                <div className="relative">
                  <FormControl>
                    <Input
                      type={showCurrentPassword ? "text" : "password"}
                      placeholder="••••••••"
                      {...field}
                    />
                  </FormControl>
                  <button
                    type="button"
                    className="absolute right-3 top-2.5 text-gray-500"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  >
                    {showCurrentPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={passwordForm.control}
            name="newPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Uus parool</FormLabel>
                <div className="relative">
                  <FormControl>
                    <Input
                      type={showNewPassword ? "text" : "password"}
                      placeholder="••••••••"
                      {...field}
                    />
                  </FormControl>
                  <button
                    type="button"
                    className="absolute right-3 top-2.5 text-gray-500"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                <FormMessage />
                <PasswordValidationDisplay errors={passwordValidationErrors} />
              </FormItem>
            )}
          />

          <FormField
            control={passwordForm.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Kinnita uus parool</FormLabel>
                <div className="relative">
                  <FormControl>
                    <Input
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="••••••••"
                      {...field}
                    />
                  </FormControl>
                  <button
                    type="button"
                    className="absolute right-3 top-2.5 text-gray-500"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              variant="outline"
              type="button"
              onClick={() => passwordForm.reset()}
            >
              Tühista
            </Button>
            <Button type="submit" disabled={isChangingPassword}>
              {isChangingPassword ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Salvestan...
                </>
              ) : (
                "Salvesta uus parool"
              )}
            </Button>
          </div>
        </form>
      </Form>

      <ValidationDialog 
        open={showValidationDialog} 
        onOpenChange={setShowValidationDialog}
        errors={passwordValidationErrors}
      />
    </>
  );
};

export default PasswordChangeForm;
